import { useSite } from '@on3/ui-lib';
import {
  ServiceLogo,
  type ServiceType,
} from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import clsx from 'clsx';
import {
  type ISortFilterItem,
  SortFilter,
} from 'components/Db/Filters/SortFilter';
import { ArrowRight } from 'components/Svg/ArrowRight';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import React, { useCallback } from 'react';

import styles from './IndustryComparisonTpColumnHeaders.module.scss';

const transferPortalHeaderList: ISortFilterItem[] = [
  {
    key: 1,
    name: 'index',
    displayName: 'Rank',
    ariaLabel: 'Transfer Portal Rank',
    flipDirection: false,
    isLogo: false,
  },
  {
    key: 3,
    name: 'name',
    ariaLabel: 'Player Name',
    displayName: 'Player',
    flipDirection: false,
    dropDownOnly: true,
    isLogo: false,
  },
  {
    key: 4,
    name: 'Industry',
    ariaLabel: 'On3 Industry',
    displayName: 'industry',
    flipDirection: false,
    dropDownOnly: false,
    isLogo: true,
  },
  {
    key: 5,
    name: 'On3',
    displayName: 'On3',
    ariaLabel: 'On3',
    flipDirection: false,
    isLogo: true,
  },
  {
    key: 6,
    name: '247',
    displayName: '247',
    ariaLabel: 'Twenty Four Seven Sports',
    flipDirection: false,
    isLogo: true,
  },

  {
    key: 7,
    name: 'Rivals',
    ariaLabel: 'Rivals',
    displayName: 'Rivals',
    flipDirection: false,
    isLogo: true,
  },
  {
    key: 8,
    name: 'status',
    ariaLabel: 'Date',
    displayName: 'Transfer Date',
    flipDirection: false,
    isLogo: false,
  },
];

interface ITeamRosterListHeaderProps {
  selected:
    | 'On3'
    | '247'
    | 'Industry'
    | 'Espn'
    | 'Rivals'
    | 'lastName'
    | 'name'
    | string;
  onChange?: React.MouseEventHandler<HTMLButtonElement>;
  ascending: boolean;
  disabled?: boolean;
  /**Pass object only for display purposes if item is not in current sort list */
  displayObject?: ISortFilterItem;
  /**Used to display player jersey number or overall rank column 1 */
  showRank?: boolean;
  /**Used to display player's status in last column */
  showStatus?: boolean;
  /**Use consensus overall rank vs jersey number */
  useRank?: boolean;
  /**Industry Comparison for Transfer Portal */
  transferPortal?: boolean;
}
export const IndustryComparisonTpColumnHeaders = ({
  selected,
  onChange,
  ascending,
  disabled,
  displayObject,
  showRank,
  showStatus,
  useRank,
  transferPortal,
}: ITeamRosterListHeaderProps) => {
  const { currentSite } = useSite();

  const siteColor =
    (currentSite?.linkColor || currentSite?.primaryColor) ?? '#fe3b1f';

  const buttonStyles = useCallback(
    (isActive: boolean) => ({ borderColor: isActive ? siteColor : '#e4e8f2' }),
    [siteColor],
  );

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      onChange?.(e);
    },
    [onChange],
  );

  return (
    <>
      <div className={styles.mobileSort}>
        <SortFilter
          ascending={ascending}
          handleSort={handleClick}
          key={`${selected}-${ascending}`}
          selected={selected}
          sortList={transferPortalHeaderList}
        />
      </div>
      <header className={styles.headerListWrapper}>
        <ul
          className={clsx(styles.headerListContainer, {
            [styles.showRank]: showRank,
          })}
        >
          {transferPortalHeaderList.map((item) => {
            const isSelected = item.name === selected;
            const isIndustry = item.name === 'Industry';
            const isRank = item.name === 'index';
            const hideRank = !showRank && isRank;

            const logo = isIndustry ? 'on3' : item.name;

            const hideColumn = hideRank;

            if (hideColumn) {
              return null;
            }

            return !item.dropDownOnly ? (
              <li className={styles.headerItem} key={item.key}>
                <PrimaryButton
                  className={clsx(styles.btnSort, {
                    [styles.active]: isSelected,
                  })}
                  data-flip={item.flipDirection}
                  data-sorttype={item.name}
                  disabled={disabled}
                  endIcon={
                    selected === item.name && (
                      <ArrowRight
                        className={clsx(styles.arrow, {
                          [styles.flip]: item.flipDirection
                            ? !ascending
                            : ascending,
                        })}
                        stroke={siteColor}
                      />
                    )
                  }
                  isLoading={disabled}
                  onClick={handleClick}
                  style={buttonStyles(isSelected)}
                  variant="text"
                >
                  {item.isLogo && (
                    <div
                      aria-label={item.ariaLabel}
                      className={styles.logoContainer}
                    >
                      <ServiceLogo type={logo as ServiceType} />
                      {isIndustry && (
                        <Text className={styles.title} component="h4">
                          {item.displayName}
                        </Text>
                      )}
                    </div>
                  )}
                  {!item.isLogo && (
                    <Text
                      aria-label={item.ariaLabel}
                      className={styles.title}
                      component="h4"
                      variant="subtitle2"
                    >
                      {item.displayName}
                    </Text>
                  )}
                </PrimaryButton>
              </li>
            ) : null;
          })}
        </ul>
      </header>
    </>
  );
};
